import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface AllocationFiltersProps {
  onFiltersChange?: (filters: {
    cloudAccount: string;
    resourceOwner: string;
  }) => void;
  data?: {
    resourceOwners: any[];
    cloudAccounts: any[];
  };
  loading?: boolean;
  error?: string | null;
  // Initial selected values (optional) for deep-linking
  initialCloudAccount?: string;
  initialResourceOwner?: string;
}

function AllocationFilters({
  onFiltersChange,
  data = { resourceOwners: [], cloudAccounts: [] },
  loading = false,
  error = null,
  initialCloudAccount,
  initialResourceOwner,
}: AllocationFiltersProps) {
  const [selectedCloudAccount, setSelectedCloudAccount] = useState('');
  const [selectedResourceOwner, setSelectedResourceOwner] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Apply incoming initial values only when they actually change,
  // so user selections are not constantly overridden.
  useEffect(() => {
    const nextCloud = initialCloudAccount ?? '';
    const nextOwner = initialResourceOwner ?? '';

    const shouldUpdateCloud = nextCloud !== selectedCloudAccount;
    const shouldUpdateOwner = nextOwner !== selectedResourceOwner;

    if (!shouldUpdateCloud && !shouldUpdateOwner) return;

    const updatedCloud = shouldUpdateCloud ? nextCloud : selectedCloudAccount;
    const updatedOwner = shouldUpdateOwner ? nextOwner : selectedResourceOwner;

    if (shouldUpdateCloud) setSelectedCloudAccount(updatedCloud);
    if (shouldUpdateOwner) setSelectedResourceOwner(updatedOwner);

    onFiltersChange?.({
      cloudAccount: updatedCloud,
      resourceOwner: updatedOwner,
    });
  }, [initialCloudAccount, initialResourceOwner]);

  const handleCloudAccountChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedCloudAccount(value);
    onFiltersChange?.({
      cloudAccount: value,
      resourceOwner: selectedResourceOwner,
    });
  };

  const handleResourceOwnerChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedResourceOwner(value);
    onFiltersChange?.({
      cloudAccount: selectedCloudAccount,
      resourceOwner: value,
    });
  };

  // Chip-style filter fields matching the design "Filters" bar (rounded,
  // light-fill, small uppercase-ish labels). Styling only — handlers unchanged.
  const fieldSx = {
    flex: '0 1 220px',
    minWidth: isMobile ? 'unset' : 180,
    '& .MuiOutlinedInput-root': {
      borderRadius: '9px',
      background: '#F9FBFE',
      fontSize: 13,
      fontWeight: 700,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E1E7F0' },
    '& .MuiInputLabel-root': {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '.3px',
      color: '#9BA6B7',
    },
  } as const;

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.25,
      }}>
      <Box
        component="span"
        sx={{
          fontSize: 12.5,
          fontWeight: 700,
          color: '#7A8497',
          mr: 0.5,
          flex: 'none',
        }}>
        Filters
      </Box>

      <FormControl size="small" sx={fieldSx}>
        <InputLabel id="allocation-cloudaccount-select-label">Cloud Account</InputLabel>
        <Select
          labelId="allocation-cloudaccount-select-label"
          id="allocation-cloudaccount-select"
          name="cloudAccount"
          value={selectedCloudAccount}
          label="Cloud Account"
          onChange={handleCloudAccountChange}
          disabled={loading}>
          <MenuItem value="">All</MenuItem>
          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            data.cloudAccounts.map((cloudAccount) => (
              <MenuItem key={cloudAccount.id} value={cloudAccount.id.toString()}>
                {cloudAccount.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <FormControl size="small" sx={fieldSx}>
        <InputLabel id="allocation-owner-select-label">Resource Owner</InputLabel>
        <Select
          labelId="allocation-owner-select-label"
          id="allocation-owner-select"
          name="resourceOwner"
          value={selectedResourceOwner}
          label="Resource Owner"
          onChange={handleResourceOwnerChange}
          disabled={loading}>
          <MenuItem value="">All</MenuItem>
          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            data.resourceOwners.map((owner) => (
              <MenuItem key={owner.id} value={owner.id.toString()}>
                {owner.firstName} ({owner.username})
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {error && (
        <Box
          sx={{
            width: '100%',
            color: 'error.main',
            fontSize: '0.875rem',
            mt: 1,
          }}>
          Error loading filter data: {error}
        </Box>
      )}
    </Box>
  );
}

export default AllocationFilters;

